import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Typography, message, Space, Tooltip, Tabs, Input, DatePicker, Row, Col, Statistic, Dropdown, Menu, Flex } from 'antd';
import { FilePdfOutlined, EyeOutlined, DownloadOutlined, PrinterOutlined, FilterOutlined, ExportOutlined, MoreOutlined, CalendarOutlined } from '@ant-design/icons';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
import InvoiceDetails from './InvoiceDetails';
import InvoicePrintModal from '../components/InvoicePrintModal';
import EmptyState from '../components/EmptyState';
import { FileTextOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Invoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('all'); // all, unpaid, paid, draft
    const [dateRange, setDateRange] = useState(null);
    const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem('invoices_pageSize') || '10'));
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Detail & Print Modals
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [printVisible, setPrintVisible] = useState(false);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/invoices', {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    limit: 100, // Load more for client-side filter
                }
            });
            setInvoices(res.data);
        } catch (error) {
            console.error(error);
            message.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setDetailsVisible(false);
                setPrintVisible(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        fetchInvoices();
    }, []);

    const handlePageSizeChange = (current, size) => {
        setPageSize(size);
        localStorage.setItem('invoices_pageSize', size.toString());
    };

    const handleView = (id) => {
        setSelectedInvoiceId(id);
        setDetailsVisible(true);
    };

    const handlePrint = (id) => {
        setSelectedInvoiceId(id);
        setPrintVisible(true);
    };

    const handleDownload = async (id, ref) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/invoices/${id}/document`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${ref}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            message.error('Failed to download PDF');
        }
    };

    const filteredInvoices = invoices.filter(inv => {
        const matchesSearch = (inv.ref || '').toLowerCase().includes(debouncedSearch.toLowerCase());
        const matchesTab =
            viewMode === 'all' ? true :
                viewMode === 'draft' ? inv.status === '0' :
                    viewMode === 'unpaid' ? (inv.status === '1' && parseFloat(inv.remain_to_pay) > 0.01) :
                        viewMode === 'paid' ? (inv.status === '2' || (inv.status === '1' && parseFloat(inv.remain_to_pay) <= 0.01)) : true;

        let matchesDate = true;
        if (dateRange && dateRange[0] && dateRange[1]) {
            const invDate = dayjs(inv.date * 1000);
            matchesDate = invDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
        }

        return matchesSearch && matchesTab && matchesDate;
    });

    const statusCounts = invoices.reduce((acc, inv) => {
        acc.all++;
        if (inv.status === '0') acc.draft++;
        else if (inv.status === '1' && parseFloat(inv.remain_to_pay) > 0.01) acc.unpaid++;
        else if (inv.status === '2' || (inv.status === '1' && parseFloat(inv.remain_to_pay) <= 0.01)) acc.paid++;
        return acc;
    }, { all: 0, draft: 0, unpaid: 0, paid: 0 });

    const totals = filteredInvoices.reduce((acc, inv) => {
        acc.total_ttc += parseFloat(inv.total_ttc || 0);
        acc.paid += parseFloat(inv.paye || 0);
        acc.remaining += parseFloat(inv.remain_to_pay || 0);
        return acc;
    }, { total_ttc: 0, paid: 0, remaining: 0 });

    const handleExportCSV = () => {
        const headers = ["Ref", "Date", "Status", "Total TTC", "Paid", "Remaining"];
        const rows = filteredInvoices.map(inv => [
            inv.ref,
            new Date(inv.date * 1000).toLocaleDateString(),
            inv.status === '0' ? 'Draft' : (inv.status === '1' && parseFloat(inv.remain_to_pay) > 0.01 ? 'Unpaid' : 'Paid'),
            inv.total_ttc,
            inv.paye,
            inv.remain_to_pay
        ]);

        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `invoices_export_${dayjs().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    const handleBulkValidate = async () => {
        const hide = message.loading('Validating selected drafts...', 0);
        try {
            const token = localStorage.getItem('token');
            const drafts = filteredInvoices.filter(inv => selectedRowKeys.includes(inv.id) && inv.status === '0');

            if (drafts.length === 0) {
                message.warning('No drafts selected');
                return;
            }

            for (const inv of drafts) {
                await axios.post(`/api/invoices/${inv.id}/validate`, {}, { headers: { Authorization: `Bearer ${token}` } });
            }
            message.success(`Validated ${drafts.length} invoices`);
            fetchInvoices();
            setSelectedRowKeys([]);
        } catch (error) {
            console.error(error);
            message.error('Bulk validation failed');
        } finally {
            hide();
        }
    };

    const handleBulkPay = async () => {
        const hide = message.loading('Processing payments...', 0);
        try {
            const token = localStorage.getItem('token');
            const unpaid = filteredInvoices.filter(inv => selectedRowKeys.includes(inv.id) && inv.status === '1' && parseFloat(inv.remain_to_pay) > 0);

            if (unpaid.length === 0) {
                message.warning('No unpaid invoices selected');
                return;
            }

            for (const inv of unpaid) {
                await axios.post(`/api/invoices/${inv.id}/payments`, {
                    amount: inv.remain_to_pay,
                    payment_mode_id: 4, // Cash
                }, { headers: { Authorization: `Bearer ${token}` } });
            }
            message.success(`Paid ${unpaid.length} invoices`);
            fetchInvoices();
            setSelectedRowKeys([]);
        } catch (error) {
            console.error(error);
            message.error('Bulk payment failed');
        } finally {
            hide();
        }
    };

    const columns = [
        {
            title: 'Ref',
            dataIndex: 'ref',
            key: 'ref',
            render: t => <b style={{ color: '#1890ff' }}>{t}</b>,
            width: 140
        },
        {
            title: 'Date', dataIndex: 'date', key: 'date',
            render: d => <span style={{ color: '#555' }}>{new Date(d * 1000).toLocaleDateString()}</span>,
            width: 120
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            render: (s, rec) => {
                if (s === '0') return <Tag color="default" style={{ borderRadius: '10px', padding: '0 10px' }}>Draft</Tag>;
                if (s === '1') {
                    if (parseFloat(rec.remain_to_pay) <= 0.001) return <Tag color="success" style={{ borderRadius: '10px', padding: '0 10px' }}>Paid</Tag>;
                    return <Tag color="warning" style={{ borderRadius: '10px', padding: '0 10px' }}>Unpaid</Tag>;
                }
                if (s === '2') return <Tag color="success" style={{ borderRadius: '10px', padding: '0 10px' }}>Paid</Tag>;
                return <Tag>Unknown</Tag>;
            },
            width: 100
        },
        { title: 'Total (TTC)', dataIndex: 'total_ttc', key: 'total_ttc', align: 'right', render: formatCurrency },
        { title: 'Paid', dataIndex: 'paye', key: 'paye', align: 'right', render: formatCurrency },
        {
            title: 'Remaining', dataIndex: 'remain_to_pay', key: 'remain_to_pay',
            align: 'right',
            render: (val, rec) => {
                if (parseFloat(val) <= 0.001) return <Tag color="green" style={{ border: 'none', background: 'transparent', fontWeight: 'bold' }}>Settled</Tag>;
                return <span style={{ color: '#cf1322', fontWeight: 'bold' }}>{formatCurrency(val)}</span>;
            }
        },
        {
            title: 'Actions', key: 'actions',
            align: 'center',
            width: 150,
            render: (_, rec) => {
                const menu = (
                    <Menu>
                        <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleView(rec.id)}>View Details</Menu.Item>
                        <Menu.Item key="print" icon={<PrinterOutlined />} onClick={() => handlePrint(rec.id)} disabled={rec.status === '0'}>Print A4</Menu.Item>
                        <Menu.Item key="download" icon={<DownloadOutlined />} onClick={() => handleDownload(rec.id, rec.ref)} disabled={rec.status === '0'}>Download PDF</Menu.Item>
                    </Menu>
                );
                return (
                    <Space>
                        <Button type="link" size="small" onClick={() => handleView(rec.id)}>View</Button>
                        <Dropdown overlay={menu} trigger={['click']}>
                            <Button type="text" icon={<MoreOutlined />} />
                        </Dropdown>
                    </Space>
                );
            }
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Flex justify="space-between" align="center" style={{ marginBottom: 32 }}>
                <div>
                    <div className="section-title">Billing</div>
                    <Title level={2} style={{ margin: 0, fontWeight: 700 }}>Invoices</Title>
                    <Typography.Text type="secondary">Manage customer invoices and payments</Typography.Text>
                </div>
                <Space size="middle">
                    <Button icon={<ExportOutlined />} onClick={handleExportCSV}>Export CSV</Button>
                    <Button type="primary" onClick={fetchInvoices} style={{ height: '40px' }}>Refresh</Button>
                </Space>
            </Flex>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="premium-card">
                        <Statistic title="Total Invoices" value={filteredInvoices.length} prefix={<FilterOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="premium-card">
                        <Statistic title="Filtered TTC" value={totals.total_ttc} suffix="JOD" valueStyle={{ color: '#3f8600' }} precision={3} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="premium-card">
                        <Statistic title="Amount Paid" value={totals.paid} suffix="JOD" valueStyle={{ color: '#1890ff' }} precision={3} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="premium-card">
                        <Statistic title="Remaining" value={totals.remaining} suffix="JOD" valueStyle={{ color: '#cf1322' }} precision={3} />
                    </Card>
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: '0 24px 24px 24px' }}>
                <div style={{ padding: '24px 0', borderBottom: '1px solid rgba(0,0,0,0.04)', marginBottom: 24 }}>
                    <Row gutter={24} align="middle">
                        <Col flex="auto">
                            <Tabs activeKey={viewMode} onChange={setViewMode} style={{ marginBottom: 0 }} className="premium-tabs">
                                <Tabs.TabPane tab={`All (${statusCounts.all})`} key="all" />
                                <Tabs.TabPane tab={`Unpaid (${statusCounts.unpaid})`} key="unpaid" />
                                <Tabs.TabPane tab={`Paid (${statusCounts.paid})`} key="paid" />
                                <Tabs.TabPane tab={`Drafts (${statusCounts.draft})`} key="draft" />
                            </Tabs>
                        </Col>
                        <Col>
                            <Space size="large">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CalendarOutlined style={{ color: '#8c8c8c' }} />
                                    <RangePicker
                                        onChange={setDateRange}
                                        style={{ borderRadius: '8px' }}
                                        ranges={{
                                            'Today': [dayjs().startOf('day'), dayjs().endOf('day')],
                                            'Yesterday': [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
                                            'Last 7 Days': [dayjs().subtract(7, 'days'), dayjs()],
                                            'Last 30 Days': [dayjs().subtract(30, 'days'), dayjs()],
                                            'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
                                        }}
                                    />
                                </div>
                                <Input.Search
                                    placeholder="Invoice Ref..."
                                    allowClear
                                    onChange={e => setSearchText(e.target.value)}
                                    style={{ width: 220 }}
                                />
                            </Space>
                        </Col>
                    </Row>
                </div>

                {selectedRowKeys.length > 0 && (
                    <div style={{ background: '#e6f7ff', padding: '12px 24px', marginBottom: 24, borderRadius: 8, border: '1px solid #91d5ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Space>
                            <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>{selectedRowKeys.length} Selected</Tag>
                            <Button type="primary" size="small" onClick={handleBulkValidate}>Validate Drafts</Button>
                            <Button size="small" onClick={handleBulkPay}>Mark Paid (Cash)</Button>
                        </Space>
                        <Button type="text" onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
                    </div>
                )}

                <Table
                    rowSelection={rowSelection}
                    dataSource={filteredInvoices}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    size="middle"
                    onRow={(record) => ({
                        onClick: () => handleView(record.id),
                    })}
                    pagination={{
                        current: 1,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        onShowSizeChange: handlePageSizeChange,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total) => `Total ${total} items`
                    }}
                    locale={{
                        emptyText: <EmptyState
                            message="No invoices found"
                            description="Try changing your search or date range."
                            icon={<FileTextOutlined />}
                        />
                    }}
                />
            </Card>


            {/* Modal Components */}
            <InvoiceDetails
                visible={detailsVisible}
                invoiceId={selectedInvoiceId}
                onCancel={() => setDetailsVisible(false)}
                onSuccess={() => {
                    fetchInvoices();
                }}
            />

            <InvoicePrintModal
                visible={printVisible}
                invoiceId={selectedInvoiceId}
                onCancel={() => setPrintVisible(false)}
                forceA4={true}
            />

        </div>
    );
};

export default Invoices;
